import * as queryString from 'query-string';
import * as cookie from 'js-cookie';
import { CookieAttributes } from 'js-cookie';
import { History, Location } from 'history';
import { includes, uniq, difference } from 'lodash';
import * as localStorage from 'local-storage';
import { useTweekValue } from 'react-tweek';
import { TEST_GROUP_LOCALSTORAGE_KEY, TEST_GROUP_LOCALSTORAGE_DELETE_KEY } from './constants';
import { AuthPath, MaybeUser } from 'packages/smart-home-support/pages/auth/models';
import { getPath as getAuthPath } from 'packages/smart-home-support/pages/auth/utils';
import { NavItem } from 'packages/smart-home-support/assets/models';
import { partnerValue, getPartner } from './partner';

function sendAnalytic(key, value) {
  const satellite = window._satellite;
  satellite.setVar(key, value);
  satellite.track('trackPage');
}

export function handleAdobePageAnalytics() {
  try {
    const virtualURL = window.location.href;
    sendAnalytic('pathName', virtualURL);
  } catch (err) {}
}

export function sendEnrollmentConfirmationAnalytics(planId) {
  try {
    sendAnalytic('Plan ID', planId);
  } catch (err) {}
}

export function getPath(path: string, page: number) {
  return `/${path}/${page}`;
}

export function normalizePhone(phoneNo: string): string {
  return `+1${phoneNo.replace(/^\+1/, '').replace(/\D/g, '')}`;
}

export function phoneToUrl(phoneNo: string): string {
  return `tel:${normalizePhone(phoneNo)}`;
}

export function formatPhone(phoneNoArg: string): string {
  const phoneNo = normalizePhone(phoneNoArg);
  return `(${phoneNo.substr(0, 3)}) ${phoneNo.substr(3, 3)}-${phoneNo.substr(6, 4)}`;
}

export const getQueryStringParsed = (location: Location): { [key: string]: any } =>
  queryString.parse((location || {}).search);

export const updateQueryString = (history: History, query: { [key: string]: any }): void =>
  history.replace({ search: queryString.stringify(query), hash: history.location.hash });

export const getCookie = (cookieName: string): string | undefined => {
  return cookie.get(cookieName);
};

export const setCookie = (cookieName: string, value: any, attrs: CookieAttributes) => {
  cookie.set(cookieName, value, attrs);
};

export const unsetCookie = (cookieName: string) => {
  cookie.remove(cookieName);
};

export const prevent = (f?: Function) => (e: React.SyntheticEvent) => {
  e.preventDefault();
  return f ? f() : undefined;
};

export const noop = () => {};

export const validateTestGroup = (value: string) => value.length < 256 && Boolean(value.match(/^[a-z0-9_,]+$/i));

export interface QueryParamToCookieOptions {
  queryParamName: string;
  cookieName?: string;
  validate: (value: string) => boolean;
  getExpirationDate?: () => Date;
  analyticName?: string;
}

export const setCookiesFromQueryParams = (
  queryParamToCookieOptions: QueryParamToCookieOptions[],
  history: History
): void => {
  const queryObject = getQueryStringParsed(history.location);

  queryParamToCookieOptions.forEach((option) => {
    const { queryParamName, cookieName, validate, getExpirationDate, analyticName } = option;
    let paramValue: string = queryObject[queryParamName];

    if (paramValue && paramValue.trim().length > 0) {
      paramValue = paramValue.trim();
      const expirationDate: Date | undefined = getExpirationDate ? getExpirationDate() : undefined;

      if (validate(paramValue)) {
        cookie.set(cookieName || queryParamName, paramValue, { expires: expirationDate });
      } else {
        console.error(`Invalid cookie value for ${queryParamName}: ${paramValue}`);
      }

      delete queryObject[queryParamName];
    }
  });

  updateQueryString(history, queryObject);
};

export const openChat = () => window.AE_SDK.triggerMessagingOverlay();

export const setChatExpertise = (...expertise: string[]) => {
  window.AE_SDK.messagingApi.overrideExpertise(expertise);
};

export const getRelativeDateInDays = (days: number) => (): Date => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
};

export const renderTemplate = (template: string, vars: Record<string, string>) =>
  Object.keys(vars).reduce(
    (renderedTemplate, k) => renderedTemplate.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), vars[k]),
    template
  );

export const getDeviceDisclaimers = () =>
  partnerValue<Record<string, string>>(
    {
      TechCoachHome: {
        router: 'Excludes any Verizon-branded or provided router or audio/video streaming device.',
        streaming: 'Excludes any Verizon-branded or provided router or audio/video streaming device.',
      },
    },
    {}
  );

export const prepareNavItems = (
  navItems: NavItem[],
  config: { vars: { claimUrl: string }; isAuthEnabled: boolean }
): NavItem[] => {
  const { vars, isAuthEnabled } = config;

  let items = navItems.map((item) => ({
    ...item,
    to: item.to ? renderTemplate(item.to, { claimUrl: vars.claimUrl || '' }) : item.to,
  }));
  if (!isAuthEnabled) {
    items = items.filter((item) => item.to !== getAuthPath(AuthPath.Login));
  }

  return items;
};

export const getCommonPagePropsForAnalytics = (props: any) => {
  const { pathname, search, hash } = props.location;
  const { partnerName, productCode } = getPartner();

  return {
    Tags: ['atlas'],
    SendAsCommonEvent: true,
    partner: partnerName,
    productCode,
    locationPath: pathname,
    locationSearch: search,
    locationHash: hash,
  };
};

export const formatTestGroupsAsArray = (testIDs: string): Array<string> => {
  const testGroups = testIDs ? testIDs.split(',') : [];
  return uniq(testGroups);
};

export const formatTestGroupsAsString = (testGroups: Array<string>) => {
  return testGroups ? uniq(testGroups).join(',') : '';
};

export const getLocalTestGroups = (): Array<string> => {
  const value: string = localStorage.get(TEST_GROUP_LOCALSTORAGE_KEY) || '';
  return formatTestGroupsAsArray(value);
};

export const getLocalTestGroupsToDelete = (): Array<string> => {
  const value: string = localStorage.get(TEST_GROUP_LOCALSTORAGE_DELETE_KEY) || '';
  return formatTestGroupsAsArray(value);
};

export const addTestGroupToSaveCache = (testId: string) => {
  const testGroupsLocal = getLocalTestGroups();
  const allTestGroups = uniq([...testGroupsLocal, testId]);
  const testGroupsFormatted = formatTestGroupsAsString(allTestGroups);

  try {
    localStorage.set(TEST_GROUP_LOCALSTORAGE_KEY, testGroupsFormatted);
  } catch (ex) {
    console.error(`Problem setting localStorage for ${testId}`);
  }
};

export const addTestGroupToDeleteCache = (testId: string) => {
  const testGroupsLocal = getLocalTestGroups();
  const testGroupsWithoutTestId = testGroupsLocal.filter((t) => t !== testId);
  const testGroupsFormatted = formatTestGroupsAsString(testGroupsWithoutTestId);
  const testGroupsLocalToDelete = getLocalTestGroupsToDelete();
  const allTestGroupsToDelete = uniq([...testGroupsLocalToDelete, testId]);
  const testGroupsToDeleteFormatted = formatTestGroupsAsString(allTestGroupsToDelete);

  try {
    localStorage.set(TEST_GROUP_LOCALSTORAGE_KEY, testGroupsFormatted);
    localStorage.set(TEST_GROUP_LOCALSTORAGE_DELETE_KEY, testGroupsToDeleteFormatted);
  } catch (ex) {
    console.error(`Problem setting localStorage for ${testId}`);
  }
};

export const updateTestGroup = (testGroupKey: string, add: string) => {
  if (add === 'true') {
    addTestGroupToSaveCache(testGroupKey);
  } else if (add === 'false') {
    addTestGroupToDeleteCache(testGroupKey);
  }
};

export const isInTestGroup = (user: MaybeUser, testGroupKey: string): boolean => {
  const isEnabled = useTweekValue(`soluto_home_web/smart_home_support/test_groups/${testGroupKey}`, false);

  if (isEnabled) {
    const localTestGroups = getLocalTestGroups();
    const localTestGroupsToDelete = getLocalTestGroupsToDelete();
    const userTestGroups = user?.testGroups || [];
    const userTestGroupsWithDeletions = difference(userTestGroups, localTestGroupsToDelete);
    const combinedTestGroups = localTestGroups.concat(userTestGroupsWithDeletions);
  
    return includes(combinedTestGroups, testGroupKey);
  }

  return false;
};

export const clearAllLocalTestGroups = (user: MaybeUser) => {
  const userTestGroups = user?.testGroups || [];
  const testGroupsLocal = getLocalTestGroups();
  const testGroupsLocalToDelete = getLocalTestGroupsToDelete();

  // Check if we can safely remove the "add" cache
  if (testGroupsLocal.length > 0 && difference(testGroupsLocal, userTestGroups).length === 0) {
    localStorage.remove(TEST_GROUP_LOCALSTORAGE_KEY);
  }

  // Check if we can safely remove the "delete" cache
  if (testGroupsLocalToDelete.length > 0) {
    if (!testGroupsLocalToDelete.some((i) => userTestGroups.includes(i))) {
      localStorage.remove(TEST_GROUP_LOCALSTORAGE_DELETE_KEY);
    }
  }
};



// WEBPACK FOOTER //
// ./packages/smart-home-support/utils.tsx